import CampaignManagementWeekday from '@/Enums/CampaignManagement/WeekDay.enum';
import type { ChannelId } from '@/Enums/ChannelId.enum';
import Color from '@/Enums/Color.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import type { ILineItemForm } from '@/Interfaces/CampaignManagement/LineItem.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import type { ITargetingLocation } from '@/Interfaces/Location.interface';
import type { IPublisher } from '@/Interfaces/Publisher.interface';
import { BeeswaxPacingLabel } from '@/constants/lineItems/beeswaxPacingLabels';
import { dateToFormat } from '@/helpers/valueFormatter/dateFormatter.helpers';
import { numberToUsFormat, numberToUsd } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { type SxProps, type Theme, capitalize } from '@mui/material';
import type { Moment } from 'moment';
import moment from 'moment';
import type { ReactNode } from 'react';

export interface IBasicInfoRow {
  value: ReactNode;
  label?: string | null;
  rowSpan?: number;
  thSx?: SxProps<Theme>;
  tdSx?: SxProps<Theme>;
}

interface IBasicInfoOptions {
  showBidCpmControl?: boolean;
  showRateCardCpmControl?: boolean;
  packageName?: string | null;
}

export default class LineItemSummaryService {
  protected static readonly baseThSx: SxProps<Theme> = {
    borderBottom: 'none',
    borderRight: '1px solid',
    borderColor: Color.GreyBorder,
  };
  //#region basic
  public static getBasicInfo(formValues: ILineItemForm, options: IBasicInfoOptions): IBasicInfoRow[] {
    const bidCpm = options.showBidCpmControl ? formValues.bidCpm : null;
    const rateCardCpm = options.showRateCardCpmControl ? formValues.product?.rateCardCpm : null;
    const { impressionsBooked, soldCpm } = formValues;

    return [
      { label: 'Line Item Name', value: formValues.name },
      { label: 'External ID/Billing ID', value: formValues.altId },
      {
        label: 'Impressions booked',
        value: impressionsBooked ? numberToUsFormat(+impressionsBooked) : null,
      },
      { label: 'Start Date - End Date', value: this.getDateRange(formValues.startDate, formValues.endDate) },
      { label: 'Client CPM', value: soldCpm ? numberToUsd(+soldCpm) : null },
      { label: 'Bid CPM', value: bidCpm ? numberToUsd(+bidCpm) : bidCpm },
      { label: 'Rate Card CPM', value: rateCardCpm ? numberToUsd(rateCardCpm) : rateCardCpm },
      { label: 'Channel', value: formValues.channel?.label },
      { label: 'Devices', value: formValues.devices?.map(opt => opt.label).join(', ') },
      { label: 'Product', value: formValues.product?.name },
      { label: 'Package', value: options.packageName },
      { label: 'Pacing', value: capitalize(BeeswaxPacingLabel[formValues.pacing] || '') },
      { label: 'Pacing Behavior', value: capitalize(formValues.pacingBehavior || '') },
      { label: 'Catchup Behavior', value: capitalize(formValues.catchupBehavior || '') },
      { label: 'Daily Impression Cap', value: formValues.dailyBudget ?? 0 },
    ].filter(row => !!row?.value);
  }

  protected static getDateRange(startDate: Moment | null, endDate: Moment | null): string {
    try {
      return [startDate, endDate]
        .map(date => dateToFormat(moment.utc(date), DateFormat.NumericMonthDayYearTime))
        .join(' - ');
    } catch {
      return '';
    }
  }
  //#endregion

  public static getDealGroupsInfo(formValues: ILineItemForm): IBasicInfoRow[] {
    const dealGroups = formValues.dealGroups ?? [];
    const rowSpan = dealGroups.length;
    return dealGroups
      .map((dealGroup, i) => ({
        ...this.getMergedRowDetails('Deal Group Name', i, rowSpan),
        value: dealGroup.label,
        thSx: this.baseThSx,
      }))
      .filter(row => !!row?.value);
  }

  public static getPublishersInfo(formValues: ILineItemForm, publishers?: IPublisher[] | null): IBasicInfoRow[] {
    const displayPublishers = (publishers ?? []).filter(publisher =>
      (formValues.publisherIds ?? []).includes(publisher.id),
    );
    const rowSpan = displayPublishers.length;
    return displayPublishers
      .map((publisher, i) => ({
        ...this.getMergedRowDetails('Publisher Name', i, rowSpan),
        value: publisher.name,
        thSx: this.baseThSx,
      }))
      .filter(row => !!row?.value);
  }

  public static getFrequencyCapsInfo(formValues: ILineItemForm): IBasicInfoRow[] {
    const capValues = formValues.frequencyCaps;
    return [
      { label: 'Hourly', value: capValues?.hourly },
      { label: 'Daily', value: capValues?.daily },
      { label: 'Weekly', value: capValues?.weekly },
      { label: 'Monthly', value: capValues?.monthly },
    ].filter(row => !!row.value);
  }

  //#region geo
  public static getGeoInfo(formValues: ILineItemForm): {
    included: IBasicInfoRow[];
    excluded: IBasicInfoRow[];
    radius: IBasicInfoRow[];
  } {
    const { included, excluded } = formValues.geo ?? {};
    const { geoFence } = formValues;

    const radius: IBasicInfoRow[] = geoFence
      ? [
          { label: 'Address', value: geoFence.address },
          { label: 'Radius', value: `${geoFence.radius} mile${geoFence.radius === 1 ? '' : 's'}` },
        ]
      : [];

    return {
      included: this.getGroupedGeoLocations(included ?? []),
      excluded: this.getGroupedGeoLocations(excluded ?? []),
      radius,
    };
  }

  protected static getGroupedGeoLocations(locations: ITargetingLocation[]): IBasicInfoRow[] {
    const map: Record<string, ITargetingLocation[]> = {};

    locations.forEach(location => {
      if (!map[location.type]?.length) {
        map[location.type] = [];
      }
      map[location.type].push(location);
    });
    return Object.entries(map).flatMap(([type, locations], typeIndex, groups) =>
      locations.map((location, locationIndex) => ({
        ...this.getMergedRowDetails(type, locationIndex, locations.length),
        value: location.displayName,
        thSx: {
          ...this.baseThSx,
          borderBottom: typeIndex === groups.length - 1 ? 'none' : undefined,
        },
      })),
    );
  }
  //#endregion

  //#region audiences
  public static getAudienceInfo(formValues: ILineItemForm): IBasicInfoRow[] {
    const { audiences, channel } = formValues;
    if (channel?.value == null) {
      return [];
    }
    const channelAudiences = audiences[channel.value as ChannelId];
    return [
      { label: 'Included', value: this.getAndGroupsDisplayValue(channelAudiences?.include) },
      { label: 'Excluded', value: this.getOrGroupDisplayValue(channelAudiences?.exclude) },
    ].filter(row => !!row.value);
  }

  protected static getAndGroupsDisplayValue(andGroups: IAudienceControlOption<number>[][] | undefined): string {
    return (andGroups ?? []).map(group => `(${this.getOrGroupDisplayValue(group)})`).join(' AND ');
  }

  protected static getOrGroupDisplayValue(orGroup: IAudienceControlOption<number>[] | undefined): string {
    return (orGroup ?? []).map(audience => audience.label).join(' OR ');
  }
  //#endregion

  //#region daypart
  public static getDaypartInfo(formValues: ILineItemForm): IBasicInfoRow[] {
    const { daypart } = formValues;
    return Object.entries(CampaignManagementWeekday)
      .map(([label, value]) => ({ label, value: this.getDaypartDisplayValue(daypart?.[value])?.join(' | ') }))
      .filter(row => !!row.value);
  }

  protected static getDaypartDisplayValue(hours: number[]): string[] | null {
    if (!hours?.length) {
      return null;
    }
    if (hours.length === 1) {
      return [this.getHourSpan(hours[0], hours[0])];
    }
    if (hours.length === 24) {
      return ['12AM to 11:59PM'];
    }

    const sortedHours = [...hours].sort((a, b) => a - b);
    const groups: string[] = [];

    let start = sortedHours[0];
    let end = sortedHours[0];

    for (const hour of sortedHours.slice(1)) {
      if (hour === end + 1) {
        end = hour;
      } else {
        groups.push(this.getHourSpan(start, end));
        start = hour;
        end = hour;
      }
    }

    groups.push(this.getHourSpan(start, end));

    return groups;
  }

  protected static getHourName(hour24: number, minutes?: string): string {
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const ampm = hour24 < 12 ? 'AM' : 'PM';
    if (minutes === undefined) {
      return `${hour12}${ampm}`;
    }
    return `${hour12}:${minutes}${ampm}`;
  }

  protected static getHourSpan(start: number, end: number): string {
    return `${this.getHourName(start)} to ${this.getHourName(end, '59')}`;
  }
  //#endregion

  protected static getMergedRowDetails(label: string, index: number, rowSpan: number) {
    const passLabel = index === 0;
    return passLabel ? { label, rowSpan } : undefined;
  }
}
